<template>
<div>
    <!-- modal -->
    <div class="modal" :class="{'is-active': modalStatus}">
      <div class="modal-background" />
      <div class="modal-content has-text-left">
        <p>
          This is our attempt at designing an online version of the 2019 <a href="https://www.southerncoalition.org/resources/racial-equity-report-cards/">Racial Equity Report Card</a> PDFs published by the <a href="https://www.southerncoalition.org/">Southern Coalition for Social Justice</a>.
        </p>
        <br>
        <p>
          This site is not affiliated with the Southern Coalition in any way. We are, however, grateful to them for publishing their reports. 
        </p>
        <br>
        <p>
          All data are <a href="https://www.southerncoalition.org/wp-content/uploads/2020/02/2019-Data-Sources.pdf">publicly available</a>. Language inspired by the original reports with heavy revisions throughout. Data crunched in Python. Reports built with Vue.js.
        </p>
        <br>
        <div class='modal-link-div'>
          <a href="#" @click="modalStatus=false">Click here to continue</a>
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="modalStatus = false"/>
    </div>


  <!-- TITLE -->
  <div class='hero-fullheight'>
    <div class='hero-top-left'>
      <a :href="footerLinkUrl">
        <img class='hero-logo' src="@/assets/icon-pie.svg" alt="Pie Chart by Alex Muravev, RU from the Noun Project">
      </a>
    </div>
    <!-- <Nav /> -->
    <div class='hero-text-centered'>
      <h1 id='hero-text'>Racial Equity Report Cards</h1>
    </div>
  </div>

  <!-- FOUR SECTIONS -->
  <LandingTile tileClass="landing-block-a" :isFullVh="true">
    <template v-slot:text>      
      North Carolina's public schools serve a diverse population of students. About half of students identify as White and about half identify as students of color. 
      <br><br>The <span id='rerc'><b>Racial Equity Report Cards</b></span> use public data to provide a snapshot of how these children are treated.
    </template>
  </LandingTile>

  <LandingTile tileClass="landing-block-b" :isFullVh="true">
    <template v-slot:text>
      In particular, these report cards focus on the <span id='stpp'><b>school-to-prison-pipeline,</b></span> the system of policies and practices that push students out of school and into the juvenile and adult criminal systems.<br><br>Once students are caught in the pipeline, it can be very difficult for them to re-engage and be successful in school.
    </template> 
  </LandingTile>

  <LandingTile tileClass="landing-block-c" :isFullVh="true">
    <template v-slot:text>
      There are many entry points in the school-to-prison-pipeline. At every step, in nearly every community, <span id='soc'><b>students of color are over-represented.</b></span>
    </template> 
  </LandingTile>

  <LandingTile tileClass="landing-block-1" :isFullVh="false" tileIcon="icon-teacher.svg" imgAlt="Teacher by Piotrek Chuchla from the Noun Project
">
    <template v-slot:title>
      School Staff
    </template>
    <template v-slot:text>
      Public school teachers in North Carolina are not representative of the students they serve. Over 78% of the teachers in the state identify as White, while only 47% of students identify as White.
    </template> 
  </LandingTile>

  <LandingTile tileClass="landing-block-2" :isFullVh="false" tileIcon="icon-test.svg" imgAlt="Test by Adrien Coquet from the Noun Project">
    <template v-slot:title>
      Academic Achievement
    </template>
    <template v-slot:text>
      Black students have different academic outcomes than White students. In grades 3 through 8, White students are twice as likely as Black students to score "College and Career Ready" on state tests.
    </template>
  </LandingTile>
  
  <LandingTile tileClass="landing-block-3" :isFullVh="false" tileIcon="icon-discipline.svg" imgAlt="Error by Deemak Daksina from the Noun Project">
    <template v-slot:title>
      School Discipline
    </template>
    <template v-slot:text>
      Black students are disciplined more often than White students. Black students make up 25% of the student population, but they receive over 55% of all short-term suspensions. This difference exists despite studies showing Black and White students misbehave at similar rates.
    </template>
  </LandingTile>

  <LandingTile tileClass="landing-block-4" :isFullVh="false" tileIcon="icon-court.svg" imgAlt="Court by John T. Garcia from the Noun Project">
    <template v-slot:title>
      Court Involvement
    </template>
    <template v-slot:text>
      Black students are refered to juvenile court more often than White students. In North Carolina, nearly half of all school-based juvenile complaints were filed against Black youth even though they make up only a quarter of the student population.
    </template>
  </LandingTile>


  <!-- SEARCH -->
  <div class='search-div-bg is-fullvh'>
    <div class='container'>
      <div class='section'>
        <div class='columns is-centered'>
          <div class='column is-8'>
            <div class='search-div'>
              <p class='tile-text'>
                We encourage everyone to examine the causes of racial inequity in their community and to develop solutions that will help young people, especially youth of color, avoid and escape the school-to-prison pipeline.
                <br><br>
                You can start by looking at <span id='lea'><b>your local school district.</b></span>
              </p>
              <b-field class='select is-primary is-fullwidth'>
                <b-autocomplete
                    v-model="userEnteredLeaName"
                    :data="filteredLeaNameArr"
                    placeholder="Search for a district by name"
                    clearable
                    open-on-focus
                    clear-on-select
                    dropdown-position="bottom"
                    @select="option => selectedLeaName = option"
                    >
                    <template slot="empty">No results found</template>
                  </b-autocomplete>
              </b-field>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

</div>

</template>


<script>
// import Nav from './Nav.vue';
import LandingTile from './LandingTile.vue';

import { mapState } from "vuex";
import { setLandingPageScrollTriggers } from "../js/scroll.js";


export default {  
  components: {
    // Nav,
    LandingTile,
  },
  created () {
    window.addEventListener('scroll', this.handleScroll);
  },
  destroyed () {
    window.removeEventListener('scroll', this.handleScroll);
  },
  mounted() {
    // animations
    setLandingPageScrollTriggers();
  },
  data() {
    return {
      userEnteredLeaName: '',
      selectedLeaName: '',
      modalStatus: true
    }
  },
  computed: {
    ...mapState(['leaNameObj']),
    footerLinkUrl() {
      return window.location.href.includes('norththird') ? 'https://norththird.io' : 'https://againwewander.com'
    },
    leaNameArr() {
      let myArr = Object.keys(this.leaNameObj);
      myArr.sort();
      return myArr
    },
    filteredLeaNameArr() {
      return this.leaNameArr.filter((option) => {
          return option
              .toString()
              .toLowerCase()
              .indexOf(this.userEnteredLeaName.toLowerCase()) >= 0
      })
    },
    selectedLeaId() {
      return this.leaNameObj[this.selectedLeaName];
    }
  },
  watch: {
    selectedLeaName() {
      this.$router.push({ name: 'Profile', params: {leaId: this.selectedLeaId}});
    }
  },
}
</script>

<style>
</style>
